import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, Plus, Search, Tags, Trash2, X } from 'lucide-react'
import { toast } from 'react-toastify'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import {
  ORG_TAGS_FETCH_LIMIT,
  appendCachedOrgTag,
  createOrgTag,
  deleteOrgTag,
  invalidateOrgTagsCache,
  listOrgTags,
  removeCachedOrgTag,
  replaceCachedOrgTag,
  updateOrgTag,
} from '../api/tags'
import useManagerStore from '../store/managerStore'
import { DEFAULT_TAG_COLOR, getTagChipStyle, slugifyTagName } from '../utils/contactTags'
import normalizeSearchText from '../utils/normalizeSearchText'

const PRESET_COLORS = [
  '#1535c8',
  '#0f766e',
  '#16a34a',
  '#ca8a04',
  '#dc2626',
  '#c026d3',
  '#7c3aed',
  '#475569',
]

function normalizeHexColor(value) {
  const raw = String(value || '').trim()
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toLowerCase()}`
  return ''
}

function normalizeManagedTag(value) {
  const source = value?.tag ?? value?.item ?? value?.data?.tag ?? value?.data?.item ?? value?.data ?? value
  if (!source || typeof source !== 'object' || Array.isArray(source)) return null
  const name = String(source.name ?? source.label ?? source.value ?? '').trim()
  if (!name) return null
  const id = source.id ?? source.tag_id
  return {
    ...source,
    id,
    name,
    slug: source.slug ?? slugifyTagName(name),
    color: normalizeHexColor(source.color) || DEFAULT_TAG_COLOR,
  }
}

function tagKey(tag) {
  return tag?.id != null ? String(tag.id) : `name:${normalizeSearchText(tag?.name)}`
}

function TagPreview({ tag }) {
  const chip = getTagChipStyle(tag, 'detail')
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-xs font-semibold ${chip.className || ''}`}
      style={chip.style}
    >
      <span className="truncate">{tag.name}</span>
    </span>
  )
}

export default function TagsPage() {
  const { t } = useTranslation()
  const tenantId = useManagerStore((s) => s.tenantId)
  const [tags, setTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [draft, setDraft] = useState({ name: '', color: DEFAULT_TAG_COLOR })
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const loadTags = useCallback(async () => {
    if (!tenantId) {
      setTags([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const items = await listOrgTags(tenantId, { skip: 0, limit: ORG_TAGS_FETCH_LIMIT })
      setTags(items.map(normalizeManagedTag).filter(Boolean))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'load_tags_page' }, extra: { tenantId } })
      toast.error(t('tagsPage.loadFailed'))
      setTags([])
    } finally {
      setLoading(false)
    }
  }, [tenantId, t])

  useEffect(() => {
    loadTags()
  }, [loadTags])

  const filteredTags = useMemo(() => {
    const normalizedQuery = normalizeSearchText(query)
    if (!normalizedQuery) return tags
    return tags.filter(tag => normalizeSearchText(tag.name).includes(normalizedQuery))
  }, [query, tags])

  const editingTag = useMemo(
    () => tags.find(tag => String(tag.id) === String(editingId)) ?? null,
    [editingId, tags],
  )

  function resetDraft() {
    setDraft({ name: '', color: DEFAULT_TAG_COLOR })
    setEditingId(null)
  }

  function startEdit(tag) {
    setEditingId(tag.id)
    setDraft({
      name: tag.name,
      color: normalizeHexColor(tag.color) || DEFAULT_TAG_COLOR,
    })
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!tenantId || saving) return

    const name = draft.name.trim()
    const color = normalizeHexColor(draft.color) || DEFAULT_TAG_COLOR
    if (!name) return

    const normalizedName = normalizeSearchText(name)
    const duplicate = tags.some(tag =>
      normalizeSearchText(tag.name) === normalizedName &&
      String(tag.id) !== String(editingId),
    )
    if (duplicate) {
      toast.error(t('tagsPage.duplicateName'))
      return
    }

    const updateBody = {
      name,
      color,
    }
    const createBody = {
      name,
      slug: slugifyTagName(name),
      color,
    }

    setSaving(true)
    try {
      if (editingId) {
        const updated = normalizeManagedTag(await updateOrgTag(tenantId, editingId, updateBody)) || {
          ...(editingTag || {}),
          id: editingId,
          ...updateBody,
        }
        setTags(prev => prev.map(tag => (String(tag.id) === String(editingId) ? updated : tag)))
        replaceCachedOrgTag(tenantId, updated)
        toast.success(t('tagsPage.updateSuccess'))
      } else {
        const created = normalizeManagedTag(await createOrgTag(tenantId, createBody))
        if (created?.id != null) {
          setTags(prev => [...prev, created])
          appendCachedOrgTag(tenantId, created)
        } else {
          invalidateOrgTagsCache(tenantId)
          await loadTags()
        }
        toast.success(t('tagsPage.createSuccess'))
      }
      resetDraft()
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: editingId ? 'update_org_tag' : 'create_org_tag' },
        extra: { tenantId, tagId: editingId, name },
      })
      toast.error(t(editingId ? 'tagsPage.updateFailed' : 'tagsPage.createFailed'))
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(tag) {
    if (!tenantId || !tag?.id || deletingId) return
    const confirmed = window.confirm(t('tagsPage.deleteConfirm', { name: tag.name }))
    if (!confirmed) return

    setDeletingId(tag.id)
    try {
      await deleteOrgTag(tenantId, tag.id)
      setTags(prev => prev.filter(item => String(item.id) !== String(tag.id)))
      removeCachedOrgTag(tenantId, tag.id)
      if (String(editingId) === String(tag.id)) resetDraft()
      toast.success(t('tagsPage.deleteSuccess'))
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'delete_org_tag' }, extra: { tenantId, tagId: tag.id } })
      toast.error(t('tagsPage.deleteFailed'))
    } finally {
      setDeletingId(null)
    }
  }

  const colorValue = normalizeHexColor(draft.color) || DEFAULT_TAG_COLOR
  const previewTag = { name: draft.name.trim() || t('tagsPage.previewName'), color: colorValue }

  return (
    <ResponsiveShell title={t('tagsPage.title')}>
      <div className="mx-auto max-w-6xl px-0 pb-10 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xl font-bold text-slate-950">
              <Tags size={22} className="text-[#1535c8]" />
              <h1 className="truncate">{t('tagsPage.title')}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {t('tagsPage.count', { count: tags.length })}
            </p>
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder={t('tagsPage.searchPlaceholder')}
            />
          </div>
        </div>

        {!tenantId ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            {t('tagsPage.needTenant')}
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
            <section className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-base font-bold text-slate-900">
                  {editingId ? t('tagsPage.editTitle') : t('tagsPage.createTitle')}
                </h2>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetDraft}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                    aria-label={t('common.cancel')}
                    title={t('common.cancel')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                    {t('tagsPage.nameLabel')}
                  </label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm text-slate-900 outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
                    value={draft.name}
                    onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
                    placeholder={t('tagsPage.namePlaceholder')}
                    maxLength={80}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">
                    {t('tagsPage.colorLabel')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorValue}
                      onChange={event => setDraft(prev => ({ ...prev, color: event.target.value }))}
                      className="h-10 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                      aria-label={t('tagsPage.colorLabel')}
                    />
                    <input
                      className="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 font-mono text-sm uppercase text-slate-900 outline-none focus:border-[#1535c8] focus:ring-2 focus:ring-[#1535c8]/10"
                      value={draft.color}
                      onChange={event => setDraft(prev => ({ ...prev, color: event.target.value }))}
                      maxLength={7}
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-8 gap-2">
                    {PRESET_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setDraft(prev => ({ ...prev, color }))}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-black/10"
                        style={{ backgroundColor: color }}
                        aria-label={t('tagsPage.pickColor', { color })}
                        title={color}
                      >
                        {colorValue === color && <Check size={15} className="text-white" strokeWidth={3} />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <TagPreview tag={previewTag} />
                </div>

                <button
                  type="submit"
                  disabled={saving || !draft.name.trim()}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#1535c8] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {editingId ? <Check size={16} /> : <Plus size={16} />}
                  {saving
                    ? t('common.saving')
                    : editingId
                      ? t('tagsPage.saveEdit')
                      : t('tagsPage.createButton')}
                </button>
              </form>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white">
              <div className="hidden overflow-hidden rounded-lg lg:block">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase text-slate-500">
                        {t('tagsPage.columns.tag')}
                      </th>
                      <th className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase text-slate-500">
                        {t('tagsPage.columns.color')}
                      </th>
                      <th className="border-b border-slate-100 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-500">
                        {t('tagsPage.columns.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400">
                          {t('common.loading')}
                        </td>
                      </tr>
                    ) : filteredTags.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400">
                          {t(query ? 'tagsPage.noResults' : 'tagsPage.empty')}
                        </td>
                      </tr>
                    ) : (
                      filteredTags.map(tag => (
                        <tr key={tagKey(tag)} className="border-b border-slate-100 last:border-b-0">
                          <td className="px-4 py-3">
                            <TagPreview tag={tag} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span
                                className="h-5 w-5 rounded-full border border-slate-200"
                                style={{ backgroundColor: tag.color || DEFAULT_TAG_COLOR }}
                              />
                              <span className="font-mono text-xs uppercase text-slate-500">{tag.color || DEFAULT_TAG_COLOR}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => startEdit(tag)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                                aria-label={t('common.edit')}
                                title={t('common.edit')}
                              >
                                <Pencil size={15} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(tag)}
                                disabled={deletingId === tag.id}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                aria-label={t('common.delete')}
                                title={t('common.delete')}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-col gap-2 p-3 lg:hidden">
                {loading ? (
                  <div className="py-8 text-center text-sm text-slate-400">{t('common.loading')}</div>
                ) : filteredTags.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-400">
                    {t(query ? 'tagsPage.noResults' : 'tagsPage.empty')}
                  </div>
                ) : (
                  filteredTags.map(tag => (
                    <div key={tagKey(tag)} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <TagPreview tag={tag} />
                        <div className="flex shrink-0 gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(tag)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600"
                            aria-label={t('common.edit')}
                            title={t('common.edit')}
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(tag)}
                            disabled={deletingId === tag.id}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-100 text-red-500 disabled:opacity-60"
                            aria-label={t('common.delete')}
                            title={t('common.delete')}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-xs font-mono uppercase text-slate-500">
                        <span
                          className="h-4 w-4 rounded-full border border-slate-200"
                          style={{ backgroundColor: tag.color || DEFAULT_TAG_COLOR }}
                        />
                        {tag.color || DEFAULT_TAG_COLOR}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </ResponsiveShell>
  )
}
